import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import AudioButton from "@/components/AudioButton";
import ConfettiBurst from "@/components/ConfettiBurst";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Check, CheckCheck, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function SharedHome() {
  const { user, refresh } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [confetti, setConfetti] = useState(0);

  const load = () => api.get("/shared/tasks").then(r => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const complete = async (task_id) => {
    try {
      const { data } = await api.post("/shared/tasks/complete", { task_id });
      await load();
      await refresh();
      setConfetti(x=>x+1);
      toast.success(`+${data.xp_awarded} XP!`);
    } catch (e) { toast.error("Failed"); }
  };

  const myField = user?.role === "child" ? "status_user" : "status_parent";
  const otherField = user?.role === "child" ? "status_parent" : "status_user";

  return (
    <div className="min-h-screen" style={{background:"linear-gradient(180deg,#FFF7ED 0%,#FFEDD5 100%)"}}>
      <TopBar variant={user?.role==="parent" ? "parent" : "child"}/>
      <ConfettiBurst trigger={confetti}/>
      <main className="max-w-5xl mx-auto p-6 sm:p-10">
        <div className="bg-white border-4 border-orange-100 rounded-3xl p-6 mb-6 flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-orange-500 text-white flex items-center justify-center"><Users className="w-8 h-8"/></div>
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-800">Shared Lane</h1>
            <p className="text-slate-600">Team up. Complete together. Earn together.</p>
          </div>
        </div>

        {active ? (
          <SharedTask task={active} onBack={()=>setActive(null)} onComplete={()=>complete(active.id)}
            mine={active[myField]==="completed"} other={active[otherField]==="completed"}/>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {tasks.map((t, i) => (
              <motion.div key={t.id} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:i*0.05}}
                className="bg-white border-4 border-orange-100 rounded-3xl p-6 shadow-xl" data-testid={`shared-task-${t.id}`}>
                <h2 className="font-display text-2xl font-bold text-slate-800">{t.title}</h2>
                <p className="text-slate-600 mt-1">{t.description_caption}</p>
                <div className="flex items-center gap-3 text-sm mt-3">
                  <StatusPill label="You" done={t[myField]==="completed"}/>
                  <StatusPill label={user?.role==="child"?"Parent":"Kid"} done={t[otherField]==="completed"}/>
                  <span className="ml-auto font-bold text-orange-600 inline-flex items-center gap-1"><Trophy className="w-4 h-4"/> {t.xp_value}</span>
                </div>
                <button onClick={()=>setActive(t)} data-testid={`open-${t.id}`}
                  className="mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white rounded-2xl py-3 font-display font-bold shadow-[0_5px_0_#EA580C]">
                  Open
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusPill({ label, done }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${done?"bg-emerald-100 text-emerald-700":"bg-slate-100 text-slate-500"}`}>
      {done ? <CheckCheck className="w-3 h-3"/> : <Check className="w-3 h-3 opacity-30"/>} {label}
    </span>
  );
}

function SharedTask({ task, onBack, onComplete, mine, other }) {
  return (
    <div className="bg-white border-4 border-orange-100 rounded-3xl p-6 shadow-xl">
      <button onClick={onBack} className="text-orange-600 font-bold mb-3" data-testid="shared-back">← Back</button>
      <h2 className="font-display text-3xl font-bold text-slate-800">{task.title}</h2>
      <p className="text-slate-600 mt-1">{task.description_caption}</p>
      {task.video_url && (
        <div className="aspect-video mt-4 rounded-2xl overflow-hidden border-4 border-orange-100 bg-black">
          {/\.(mp4|webm|ogg)(\?|$)/i.test(task.video_url) ? (
            <video src={task.video_url} controls className="w-full h-full"/>
          ) : (
            <iframe src={task.video_url} title={task.title} className="w-full h-full" allowFullScreen/>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <AudioButton text={task.audio_instructions} voice="parent" label="Hear instructions" testId="shared-audio"/>
        <div className="flex gap-2">
          <StatusPill label="You" done={mine}/>
          <StatusPill label="Partner" done={other}/>
        </div>
      </div>
      {!mine && (
        <button onClick={onComplete} data-testid="shared-complete"
          className="mt-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl px-6 py-3 font-display font-bold shadow-[0_5px_0_#047857]">
          Mark as done
        </button>
      )}
      {mine && other && <p className="mt-5 text-emerald-600 font-bold">Both done! Reward earned!</p>}
    </div>
  );
}
